import { useState } from 'react';
import { CopyToClipboard } from '@gonfalon/ui/CopyToClipboard';
import { Button, IconButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { trackSdkKeyCopied, trackSdkKeyViewToggled } from '../analytics';
import type { SdkKeySource } from '../types';

import styles from './EnvironmentKeysTable.module.css';

type KeyValueCellProps = {
  value: string;
  isMaskable: boolean;
  /** When set, copy and reveal fire the shared "SDK Keys" analytics. */
  source?: SdkKeySource;
  /** Toast announced after a copy. Defaults to "Copied!". */
  copiedMessage?: string;
  copyAriaLabel?: string;
  /** Wrap the full value instead of ellipsis-truncating — for reveal-once secret flows. */
  wrapValue?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
};

// Canonical masked click-to-copy key value, shared by the SDK key and access token surfaces.
export function KeyValueCell({
  value,
  isMaskable,
  source,
  copiedMessage,
  copyAriaLabel,
  wrapValue,
  onVisibilityChange,
}: KeyValueCellProps) {
  const resolvedCopyAriaLabel =
    copyAriaLabel !== undefined
      ? copyAriaLabel
      : source !== undefined
        ? `Copy ${source} to clipboard`
        : 'Copy to clipboard';
  // Remember which value was revealed: deriving visibility from the current value means a
  // rotated secret can never render revealed. A value the user already revealed this session
  // shows again if it returns (environment switch away and back) — that's intended.
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const isVisible = revealedValue === value;
  const showValue = !isMaskable || isVisible;
  // One bullet per character: the code font is monospace, so revealing doesn't shift the layout
  const maskedValue = '•'.repeat(value.length);

  return (
    <div className={clsx(styles.keyValue, { [styles.wrap]: wrapValue })}>
      <CopyToClipboard
        text={value}
        message={copiedMessage}
        onCopy={() => {
          if (source !== undefined) {
            trackSdkKeyCopied({ source });
          }
        }}
      >
        <Button className={styles.copyButton} variant="minimal" aria-label={resolvedCopyAriaLabel}>
          <span className={clsx(styles.valueText, 'fs-exclude', { [styles.masked]: !showValue })}>
            {showValue ? value : maskedValue}
          </span>
          <Icon name="copy-clipboard" size="small" slot="end-icon" />
        </Button>
      </CopyToClipboard>
      {isMaskable && (
        <IconButton
          icon={isVisible ? 'visibility-off' : 'visibility'}
          size="small"
          variant="minimal"
          aria-label={isVisible ? 'Hide value' : 'Show value'}
          onPress={() => {
            const nextIsVisible = !isVisible;
            if (source !== undefined) {
              trackSdkKeyViewToggled({ source, isVisible: nextIsVisible });
            }
            setRevealedValue(nextIsVisible ? value : null);
            onVisibilityChange?.(nextIsVisible);
          }}
        />
      )}
    </div>
  );
}
